import { isArray, isNumber, isObject } from '@/helpers/is';

const FILTER_PREFIX = 'filter_';
const CHECKBOX_COLUMN_WIDTH = '50px';
const MAX_ROWS_IN_CALL = 100;

/**
 * get range object data. for example: { start: 1, end: 100 }
 * @param {number} val
 * @returns {object} { start:, end: }
 */
const getRangeDataByNumber = (val = 1) => {
  let index = 1;
  while (val > index * MAX_ROWS_IN_CALL) {
    index += 1;
  }

  const end = index * MAX_ROWS_IN_CALL;
  const start = end - (MAX_ROWS_IN_CALL - 1);

  return [start, end];
};

/**
 * Clean query keys from prefix
 * @param {object} d data
 * @returns {object}
 */
const getCleanQuery = (d = {}) => {
  const cleanQuery = {};
  Object.keys(d).forEach((key) => {
    const cleanKey = String(key).replace(FILTER_PREFIX, '');
    cleanQuery[cleanKey] = d[key];
  });

  return cleanQuery;
};

const removeEmptyFilters = (filters) => Object.fromEntries(
  // eslint-disable-next-line no-unused-vars
  Object.entries(filters).filter(([_, value]) => value !== ''
    && value !== null && value !== undefined),
);

const initialColumnFilters = (mockColumns) => mockColumns
  .filter((column) => column.accessor !== 'actions')
  .reduce((acc, column) => {
    acc[column.accessor] = '';
    return acc;
  }, {});

/**
 * Clean query keys from prefix
 * @param {object} d data
 * @returns {object}
 */
const getDirtyQuery = (d = {}) => {
  const dirtyQuery = {};
  Object.keys(d).forEach((key) => {
    const dirtyKey = `${FILTER_PREFIX}${String(key)}`;
    dirtyQuery[dirtyKey] = d[key];
  });

  return dirtyQuery;
};

/**
 * calculate total pages
 * @param {number} d total items count
 * @param {number} perPage items per page
 * @returns {number}
 */
const calculateTotalPages = (d = 0, perPage = 10) => Math.ceil(d / perPage);

/**
 * get row start & end index, and total number of rows.
 * helper function for pagination text
 * @param {number} p page number
 * @param {number} perPage rows per page
 * @param {number} total total number of rows
 * @returns {array} [start, end, total]
 */
const getPageCountDetails = (p = 1, perPage = 10, total = 0) => {
  const [start, end] = getRowsIndexes(p, perPage);
  const pageEnd = (end > total) ? total : end;
  return [start, pageEnd, total];
};

/**
 * get rows start and end index according to page
 * @param {number} p page number
 * @param {perPage} p rows per page
 * @returns {array} example: for page 2 the result is [11, 20]
 */
const getRowsIndexes = (p = 1, perPage = 10) => {
  const startIndex = ((p - 1) * perPage) + 1;
  const endIndex = p * perPage;

  return [startIndex, endIndex];
};

/**
 * get all elements needed to show on table
 * @param {number} start
 * @param {number} end
 * @param {array} d values
 * @returns {array}
 */
const getRowsData = (start, end, d = []) => {
  if (!isNumber(start) || !isNumber(end)) {
    // eslint-disable-next-line no-console
    console.error('One of the variables (start or end) is not a number. cant get rows data');
    return [];
  }

  const startIndex = (start - 1 >= 0) ? start - 1 : 0;
  return d.slice(startIndex, end);
};

/**
 * validate table data and log error if needed
 * @param {array} data
 * @param {number} count
 * @returns {boolean}
 */
const validateData = (data, count) => {
  if (!isArray(data)) {
    // eslint-disable-next-line no-console
    console.error('Call to "getData" function did not return "data" property as array', data);
    return false;
  }

  if (!isNumber(count)) {
    // eslint-disable-next-line no-console
    console.error('Call to "getData" function did not return "count" property as number', count);
    return false;
  }

  if (data.some((e) => (e.id === undefined && e._id === undefined))) {
    // eslint-disable-next-line no-console
    console.error('table component is missing [id] and [_id] property in one of the data prop array items');
    return false;
  }

  if (data.some((e) => (!isObject(e)))) {
    // eslint-disable-next-line no-console
    console.error('array from "getData" function contain an element that is not object');
    return false;
  }

  return true;
};

const getNestedValue = (obj, path) => path.split('.').reduce((acc, part) => acc?.[part], obj);

const extractFilterOptions = (data, columns) => {
  const newFilterOptions = {};

  const accessors = columns
    .map((col) => (typeof col === 'string' ? col : col.accessor))
    .filter((val) => typeof val === 'string');

  accessors.forEach((accessor) => {
    newFilterOptions[accessor] = new Set();
  });

  data.forEach((item) => {
    accessors.forEach((accessor) => {
      const value = getNestedValue(item, accessor);

      if (Array.isArray(value)) {
        value.forEach((v) => {
          if (v !== undefined && v !== null) {
            newFilterOptions[accessor].add(v.toString());
          }
        });
      } else if (value !== undefined && value !== null) {
        newFilterOptions[accessor].add(value.toString());
      }
    });
  });

  return Object.fromEntries(
    Object.entries(newFilterOptions).map(([key, set]) => [key, [...set]]),
  );
};

const isEmpty = (val) => val === undefined || val === null || val === '';

/**
 * Sorts data with special handling for empty values.
 *
 * @param {Array} data - Array of data objects.
 * @param {Object} sort - Sorting options with "columnAccessor" and "direction".
 * @returns {Array} Sorted data.
 */
const sortWithEmptyHandling = (data, sort) => {
  if (!sort?.columnAccessor || !sort?.direction) return data;

  const { columnAccessor, direction } = sort;

  return [...data].sort((a, b) => {
    const aVal = a[columnAccessor];
    const bVal = b[columnAccessor];

    const aIsEmpty = isEmpty(aVal);
    const bIsEmpty = isEmpty(bVal);

    // Handle empty values.
    if (aIsEmpty && !bIsEmpty) return direction === 'asc' ? -1 : 1;
    if (!aIsEmpty && bIsEmpty) return direction === 'asc' ? 1 : -1;
    if (aIsEmpty && bIsEmpty) return 0;

    // For string values, use localeCompare.
    if (typeof aVal === 'string' && typeof bVal === 'string') {
      return direction === 'asc'
        ? aVal.localeCompare(bVal)
        : bVal.localeCompare(aVal);
    }

    // For non-string values, compare directly.
    if (direction === 'asc') {
      if (aVal > bVal) return 1;
      if (aVal < bVal) return -1;
      return 0;
    }
    if (aVal < bVal) return 1;
    if (aVal > bVal) return -1;
    return 0;
  });
};

/**
 * Checks if two dates refer to the same calendar day.
 *
 * @param {Date|string|number} d1 - First date.
 * @param {Date|string|number} d2 - Second date.
 * @returns {boolean} True if same day.
 */
const isSameDay = (d1, d2) => {
  const date1 = new Date(d1);
  const date2 = new Date(d2);
  return (
    date1.getFullYear() === date2.getFullYear()
    && date1.getMonth() === date2.getMonth()
    && date1.getDate() === date2.getDate()
  );
};

/**
 * Checks if a date is within a date range.
 *
 * @param {Date|string|number} date - The date to check.
 * @param {Date} start - Start date.
 * @param {Date} end - End date.
 * @returns {boolean} True if in range.
 */
const isDateInRange = (date, start, end) => {
  const d = new Date(date);
  return d >= new Date(start) && d <= new Date(end);
};

/**
 * Filters and sorts data based on provided filters and sort options.
 * Supports filtering by a single date
 * (as Date instance, valid date string, or an array with one Date)
 * or a date range (array with two Dates).
 *
 * @param {Array} data - Array of data objects.
 * @param {Object} filters - Object with key/value pairs for filtering.
 * @param {Object} sort - Sorting options with "columnAccessor" and "direction".
 * @returns {Array} Filtered and sorted data.
 */
const filterAndSortData = (data, filters, sort) => {
  let filtered = data;

  if (filters && typeof filters === 'object') {
    filtered = filtered.filter((item) => Object.entries(filters).every(([key, value]) => {
      // Skip filtering if filter value is undefined, null, or an empty string.
      if (isEmpty(value)) return true;

      const originalVal = item[key];

      const originalString = String(originalVal).toLowerCase();
      const filterString = String(value).toLowerCase();

      // Handle boolean values with Hebrew string representation.
      if (typeof originalVal === 'boolean') {
        const isHebrewBool = (filterString === 'כן' && originalVal === true) || (filterString === 'לא' && originalVal === false);
        return isHebrewBool;
      }

      if (typeof originalVal === 'number') {
        return originalString.includes(filterString);
      }

      // Check if value is an array of two Dates for range filtering.
      // eslint-disable-next-line max-len
      if (Array.isArray(value) && value.length === 2 && value[0] instanceof Date && value[1] instanceof Date) {
        return isDateInRange(originalVal, value[0], value[1]);
      }

      // Check if value is an array with a single Date for same-day filtering.
      if (Array.isArray(value) && value.length === 2 && value[0] instanceof Date) {
        return isSameDay(value[0], originalVal);
      }

      // Check for single date filtering: value as Date instance or valid date string.
      let filterDate = null;
      if (value instanceof Date) {
        filterDate = value;
      } else if (typeof value === 'string' && !Number.isNaN(Number(Date.parse(value)))) {
        filterDate = new Date(value);
      }
      if (filterDate) {
        return isSameDay(filterDate, originalVal);
      }

      // Fallback: Check if the original value includes the filter string.
      return originalString.includes(filterString);
    }));
  }

  return sortWithEmptyHandling(filtered, sort);
};

export {
  FILTER_PREFIX,
  CHECKBOX_COLUMN_WIDTH,
  getRangeDataByNumber,
  getCleanQuery,
  getDirtyQuery,
  calculateTotalPages,
  getRowsIndexes,
  getRowsData,
  validateData,
  getPageCountDetails,
  removeEmptyFilters,
  extractFilterOptions,
  initialColumnFilters,
  filterAndSortData,
};
